import { gql } from "../__generated__";

export const CREATE_WORKER = gql(`
    mutation CreateWorkerProfile($workerProfile: WorkerProfileInput!) {
        createWorkerProfile(workerProfile: $workerProfile) {
            ...WorkerProfileFields
        }
    }
`);

export const CREATE_CLIENT_PROFILE = gql(`
    mutation CreateClientProfile($clientProfile: ClientProfileInput!) {
        createClientProfile(clientProfile: $clientProfile) {
            ...ClientProfileFields
        }
    }
`);

export const CREATE_PRIVATE_CLIENT_PROFILE = gql(`
    mutation CreatePrivateClientProfile($privateClientProfile: PrivateClientProfileInput!) {
        createPrivateClientProfile(privateClientProfile: $privateClientProfile) {
            ...ClientProfileFields
        }
    }
`);

gql(`
    fragment MissionInfoFields on MissionInfo {
        clientInfo {
            ...ClientInfoFields
        }
        name
        earningType
        payrollSource
        activityType {
            title
            ofsSalariumId
            ofsSalariumGroupId
            id
        }
        workerInfo {
            ...WorkerInfoFields
        }
        id
        workingTimeClassicSpec {
            repetition
            unit
            unitCount
            dailyHours
        }
    }
`);

gql(`
    fragment DayScheduleFields on DayScheduleType {
        freeDurations {
            type
            duration
        }
        timeMode
        fixedTimes {
            endTime
            startTime
            type
        }
    }
`);

gql(`
    fragment MissionFeeFields on MissionFee {
        id
        type
        amount
        invoiceAmount
        repetition
        otherTitle
        externalId
    }
`);

gql(`
    fragment MissionDeductionFields on MissionDeduction {
        id
        amount
        repetition
        description
        externalId
    }
`);

gql(`
    fragment CoreContractFields on Contract {
        id
        state
        signingState
        signatureStatus
        startDate
        effectiveEndDate
        endDate
        workerAcceptedAt
        clientAcceptedAt
        workerContractSignedAt
        workerContractSealedAt
        clientContractSignedAt
        clientContractSealedAt
        cctEditionId
        cctContractId
        jobTitle
        jobDescription
        cctDetails {
            ...CCTDetailsFieldFullValueFields
        }
        earningType
        workingPercentage
        workingAddress {
            ...AddressFields
        }
        useClientWorkingAddress
        baseSalary
        grossSalary
        netSalary
        invoiceSalary
        has13thSalary
        vacationDays
        publicHolidaysCompensation
        thirteenMonthCompensation
        vacationCompensation
        lseMargin
        clientDeductions {
                title
                quantity
                rate
                amount
        }
        workerDeductions {
                title
                quantity
                rate
                amount
        }
        hoursPerWeekFullTime
        terminatedBy
        terminatedDate
        terminationReason
        terminationOwner {
            id
            avatarUrl
            firstName
            lastName
        }
        terminationOtherReason
        createdAt
        updatedAt
        contractEffectiveAsset {
            ...AssetFields
        }
        workingTimeClassicSpec {
            repetition
            unit
            unitCount
            dailyHours
        }
        fees {
            ...MissionFeeFields
        }
        deductions {
            ...MissionDeductionFields
        }
        lppPlan {
          ...LPPPlanFields
        }
        ijmInfo {
            ijmGroup {
                ...IjmGroupField
            }
            payFirstDays
            payRemainingPercents
        }
    }
`);

gql(`
    fragment CoreMissionFields on Mission {
        id
        name
        educationLevel
        createdAt
        updatedAt
        laaActivityType
        payrollSource
        legalEmployer {
            id
            name
            marketingName
            useLseCct
        }
        activityType {
            id
            title
            ofsSalariumId
            ofsSalariumGroupId
        }
        clientInfo {
            ...ClientInfoFields
        }
        workerInfo {
            ...WorkerInfoFields
        }
        currentContract {
            ...CoreContractFields
        }
        documents {
            id
            type
            title
            documentUrl
        }
    }
`);

gql(`
    fragment MissionCancellationRequestFields on MissionCancellationRequest {
        id
        createdAt
        reason
        otherReason
        isCancelledBySelf
        requestedRole
    }
`);

export const GET_MISSION = gql(`
    query getMission($id: ID!) {
        getMission(id: $id) {
            ...CoreMissionFields
        }
    }
`);

export const CREATE_MISSION = gql(`
    mutation CreateMission($missionData: MissionInput!, $generateContractAsset: Boolean) {
        createMission(
            missionData: $missionData
            generateContractAsset: $generateContractAsset
        ) {
            ...CoreMissionFields
        }
    }
`);

export const UPDATE_MISSION = gql(`
    mutation UpdateMission(
        $id: ID!
        $missionData: MissionInput!
        $generateContractAsset: Boolean
    ) {
        updateMission(
            id: $id
            missionData: $missionData
            generateContractAsset: $generateContractAsset
        ) {
            ...CoreMissionFields
        }
    }
`);

export const UPDATE_MISSION_DOCUMENTS = gql(`
    mutation UpdateMissionDocuments(
        $missionId: ID!
        $documents: [DocumentInput!]!
    ) {
        updateMissionDocuments(
            missionId: $missionId
            documents: $documents
        ) {
            ...CoreMissionFields
        }
    }
`);

export const CHANGE_MISSION_OWNER = gql(`
    mutation ChangeMissionOwner(
        $missionId: ID!
        $clientUserId: ID!
    ) {
        changeMissionOwner(
            missionId: $missionId
            clientUserId: $clientUserId
        ) {
            ...CoreMissionFields
        }
    }
`);

gql(`
    fragment AuditLogFields on AuditLog {
        id
        text
        createdAt
        userAvatarUrl
        userDisplayName
        event
    }
`);

export const FIND_MISSION_AUDIT_LOGS = gql(`
    query FindMissionAuditLogs($missionId: ID!, $offset: Int, $limit: Int) {
        findMissionAuditLogs(missionId: $missionId, offset: $offset, limit: $limit) {
            elements {
                ...AuditLogFields
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const GET_CONTRACT = gql(`
    query getContract($id: ID!) {
        getContract(id: $id) {
            ...CoreContractFields
            missionInfo {
                id
                workerInfo {
                    ...WorkerInfoFields
                }
                clientInfo {
                    ...ClientInfoFields
                }
            }
        }
    }
`);

export const GET_TERMINATION_INFO = gql(`
    query getTerminationInfo($missionId: ID!) {
        getTerminationInfo(missionId: $missionId) {
            newState
            minimalEndDate
            cancellationRequest {
                ...MissionCancellationRequestFields
            }
        }
    }
`);

export const DELETE_MISSION = gql(`
    mutation DeleteMission($missionId: ID!) {
        deleteMission(missionId: $missionId)
    }
`);

export const TERMINATE_MISSION = gql(`
    mutation TerminateMission($missionId: ID!, $data: TerminationDataInput!) {
        terminateMission(missionId: $missionId, data: $data) {
            ...CoreMissionFields
        }
    }
`);

export const FIND_ACTIVITY_TYPE = gql(`
    query FindActivityType($query: String!, $offset: Int, $limit: Int) {
        findActivityType(query: $query, offset: $offset, limit: $limit) {
            elements {
                id
                title
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const GET_PRIVATE_CLIENT_ACTIVITY_TYPES = gql(`
    query GetPrivateClientActivityTypes($stateCode: String) {
        getPrivateClientActivityTypes(stateCode: $stateCode) {
            key
            name
            laaActivityType
            activityTypeId
            cctEditionId
        }
    }
`);

export const FIND_CLIENT_INFO = gql(`
    query FindClientInfo($query: String!) {
        findClientInfo(query: $query) {
            elements {
                ...ClientInfoFields
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const UPDATE_WORKER_PROFILE = gql(`
    mutation updateWorkerProfile($changes: WorkerProfileInput!) {
        updateWorkerProfile(workerProfile: $changes) {
            ...WorkerProfileFields
        }
    }
`);

export const UPDATE_CLIENT_PROFILE = gql(`
    mutation updateClientProfile($changes: ClientProfileInput!) {
        updateClientProfile(clientProfile: $changes) {
            ...ClientProfileFields
        }
    }
`);

export const UPDATE_PRIVATE_CLIENT_PROFILE = gql(`
    mutation updatePrivateClientProfile($changes: PrivateClientProfileInput!) {
        updatePrivateClientProfile(privateClientProfile: $changes) {
            ...ClientProfileFields
        }
    }
`);

export const FIND_WORKER_INFO = gql(`
    query FindWorkerInfo($query: String!) {
        findWorkerInfo(query: $query, limit: 100) {
            count
            elements {
                ...WorkerInfoWithMissionsFields
            }
        }   
    }
`);

export const GET_WORKER_INFO = gql(`
    query GetWorkerInfo($workerId: ID, $phoneNumber: String) {
        getWorkerInfo(workerProfileId: $workerId, phoneNumber: $phoneNumber) {
            ...WorkerInfoWithMissionsFields
        }
    }    
`);

export const GET_CLIENT_INFO = gql(`
    query GetClientInfo($clientProfileId: ID!) {
        getClientInfo(clientProfileId: $clientProfileId) {
            ...ClientInfoFields
        }
    }    
`);

export const REMOVE_WORKER_FROM_CLIENT = gql(`
    mutation RemoveRelationship($workerProfileId: ID!) {
        removeWorkerFromClient(workerProfileId: $workerProfileId)
    }
`);

export const REGENERATE_CONTRACT_ASSETS = gql(`
    mutation RegenerateContractAssets2($contractId: ID!) {
        regenerateContractAssets2(contractId: $contractId) {
            clientAssetId
            workerAssetId
        }
    }
`);

export const GET_CURRENT_LEGAL_EMPLOYER = gql(`
    query getCurrentLegalEmployer {
        getCurrentLegalEmployer {
            id
            name
            marketingName
            useLseCct
        }
    }
`);

export const CHANGE_MISSION_SIGNER = gql(`
    mutation ChangeMissionSigner(
        $missionId: ID!
        $clientUserId: ID!
    ) {
        changeMissionSigner(
            missionId: $missionId
            clientUserId: $clientUserId
        ) {
            ...CoreMissionFields
        }
    }
`);
